import { Form, formProviderHOC } from '@sravni/cosago-react-library/lib/components';
import { useFormContext } from '@sravni/cosago-react-library/lib/hooks';
import type { FormComponentProps } from '@sravni/cosago-react-library/lib/types';
import { Button } from '@sravni/react-design-system';
import { useIsMobile } from '@sravni/react-utils';
import type { FC } from 'react';

import { useAppSelector } from 'shared/lib';
import { useCommonFormBodyHooks } from 'shared/lib/useCommonFormBodyHooks';
import type { TCommonAdditionalFormProps } from 'shared/types';
import { FormStickyButton } from 'shared/ui';
import { FormWithControlledNavigation } from 'shared/ui/FormWithControlledNavigation';

import { useSendFieldErrorAnalyticsEvent } from 'entities/creditForm/lib/useSendFieldErrorAnalyticsEvent';
import { isLoggedSelector } from 'entities/user';
import { type TContactsFormFields } from 'src/client/entities/creditForm';
import { useDevFillMockDataTool } from 'src/client/entities/creditForm/lib/useDevFillMockDataTool';

import { ContactsInfoTexts } from 'features/UpdateContactsInfo';

import { FormFieldsNavigationSequence } from '../../ContactsInfo.config';
import { useAccordionHeaderParams } from '../../lib/useAccordionHeaderParams';

type TContactsInfoFormView = TCommonAdditionalFormProps & {
  shouldRestoreAdditionalData: boolean;
};

type Props = FormComponentProps<TContactsFormFields, TContactsInfoFormView>;

const ContactsInfoFormView: FC<Props> = ({
  isDialog,
  onSubmit,
  NAMES,
  submitButtonText,
  FieldConstructor,
  isLoading,
  additionalProps,
}) => {
  const { setHeader, isFormForceOpened, formId, isAnyFormStepOpenedInPopup } = additionalProps || {};
  const isAuth = useAppSelector(isLoggedSelector);
  const isMobile = useIsMobile();
  const isStickyButton = !isDialog && isMobile && !isAnyFormStepOpenedInPopup;
  const isShowInlineSubmitButton = isAuth && !isMobile && !isDialog;

  const { watch } = useFormContext<TContactsFormFields>();

  const [title, subtitle] = useAccordionHeaderParams(watch(), {}, false);

  useSendFieldErrorAnalyticsEvent<TContactsFormFields>(2, ContactsInfoTexts);
  useDevFillMockDataTool<TContactsFormFields>({
    type: 'contactsData',
    stepLabel: 'Контакты',
  });

  const { controls } = useCommonFormBodyHooks({
    currentControls: FormFieldsNavigationSequence,
    isFormForceOpened,
    headerData: {
      title,
      subtitle,
    },
    setHeader,
  });

  return (
    <FormWithControlledNavigation
      onSubmit={onSubmit}
      controlElement={FieldConstructor}
      isDialog={isDialog}
      isLoading={isLoading}
      submitButtonText={submitButtonText}
      navigationControls={controls}
      withSubmitButtons={isDialog}
      formId={formId}
    >
      <Form.Block>
        <Form.Row>
          <FieldConstructor type={NAMES.mobilePhone} />
          {isAuth && <FieldConstructor type={NAMES.email} />}
          {isShowInlineSubmitButton && (
            <Button
              type="submit"
              size={60}
              variant="primary"
            >
              {submitButtonText}
            </Button>
          )}
          <FormStickyButton
            onClick={onSubmit}
            enabled={isStickyButton && isAuth}
          >
            {submitButtonText}
          </FormStickyButton>
        </Form.Row>
      </Form.Block>
    </FormWithControlledNavigation>
  );
};

export const ContactsInfoViewHoc = formProviderHOC<TContactsFormFields, TContactsInfoFormView>(ContactsInfoFormView);
